import { lazy, StrictMode, type ComponentType } from 'react'
import { createRoot } from 'react-dom/client'
import './styles/index.css'
import './styles/print.css'
import 'katex/dist/katex.min.css'
import App from './App'
import { consumeImpersonationHash } from '@/host/bootstrap/consumeImpersonationHash'
import { registerServiceWorker, reloadAppSafely } from '@/shared/lib/offline/pwa-register'

const AdminRouter = lazy(() => import('@/admin/router'))
const HostRouter = lazy(() => import('@/host/router'))

const routerMap: Record<typeof __APP_TYPE__, ComponentType> = {
  admin: AdminRouter,
  host: HostRouter,
}

const Router = routerMap[__APP_TYPE__] ?? AdminRouter

// Must run before the router mounts so the auth store rehydrates as
// authenticated and ProtectedRoute doesn't bounce to /login.
if (__APP_TYPE__ === 'host') {
  consumeImpersonationHash()
  // 86exwdkr5 PR-1: PWA service worker. No-op in dev + admin.
  registerServiceWorker()
}

// A lazily-imported chunk (e.g. HostDashboard) can fail to load when a new
// version is deployed while this tab is still open: the hashed filename it
// remembers no longer exists on the server, so the import 404s and React Router
// shows "error loading dynamically imported module". Vite fires
// `vite:preloadError` for exactly this; reload once to pull the fresh
// index.html (which references the new chunk hashes). The sessionStorage guard
// stops a reload loop if the chunk is genuinely missing, so the error still
// surfaces after a single attempt instead of looping. FO-20: the reload waits
// while a dialog is open or a field has focus, so typed work is not lost.
window.addEventListener('vite:preloadError', () => {
  const KEY = 'vite-preload-reloaded-at'
  let last = 0
  try {
    last = Number(sessionStorage.getItem(KEY) || '0')
  } catch {
    /* storage blocked: fall through with no guard history */
  }
  if (Date.now() - last > 10_000) {
    try {
      sessionStorage.setItem(KEY, String(Date.now()))
    } catch {
      /* storage blocked */
    }
    reloadAppSafely()
  }
})

const root = document.getElementById('root')
if (!root) throw new Error('#root element not found')

createRoot(root).render(
  <StrictMode>
    <App router={<Router />} />
  </StrictMode>,
)
